/**
 * Main IdP Client class module
 */

import { setupFormValidation, validateField } from './form-validation.js';
import { setupLanguageSwitcher } from './language.js';
import { setupPinCodeInput, updateTOTPCode, showPinError } from './totp.js';
import { setupMobileFeatures } from './mobile.js';
import { isPhone } from './phone.js';

export class IdPClient {
    constructor() {
        this.baseURL = window.location.origin;
        this.isMobile = isPhone();
        this.init();
    }

    init() {
        this.setupEventListeners();
        setupFormValidation();
        setupMobileFeatures();
        this.detectAndApplyTheme();
    }

    setupEventListeners() {
        const loginForm = document.getElementById('loginForm');
        if (loginForm) {
            loginForm.addEventListener('submit', this.handleLoginSubmit.bind(this));
        }

        const testOAuth2Btn = document.getElementById('testOAuth2');
        if (testOAuth2Btn) {
            testOAuth2Btn.addEventListener('click', () => {
                import('./oauth2.js').then(module => module.testOAuth2Flow());
            });
        }

        setupPinCodeInput();

        if (!this.isMobile) {
            // Prefer input with autofocus attribute, then fall back to username field or first input
            const autofocusInput = document.querySelector('input[autofocus]');
            const usernameInput = document.getElementById('username');
            const firstInput = autofocusInput || usernameInput || document.querySelector('input[type="text"], input[type="password"]');
            if (firstInput && !firstInput.classList.contains('pin-digit')) {
                firstInput.focus();
            }
        }

        setupLanguageSwitcher();

        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.isMobile = isPhone();
                this.handleResize();
            }, 250);
        });
    }

    handleResize() {
        this.isMobile = isPhone();
    }

    detectAndApplyTheme() {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const savedTheme = localStorage.getItem('theme');

        if (savedTheme) {
            document.body.setAttribute('data-theme', savedTheme);
        } else if (prefersDark) {
            document.body.setAttribute('data-theme', 'dark');
        }

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
            if (!localStorage.getItem('theme')) {
                document.body.setAttribute('data-theme', e.matches ? 'dark' : 'light');
            }
        });
    }

    handleLoginSubmit(event) {
        const form = event.target;
        const submitBtn = form.querySelector('button[type="submit"]');

        const pinDigits = form.querySelectorAll('.pin-digit');
        if (pinDigits.length > 0) {
            updateTOTPCode(pinDigits);
            const totpCode = document.getElementById('totp_code').value;

            if (totpCode.length !== 6 || !/^\d{6}$/.test(totpCode)) {
                event.preventDefault();
                showPinError(pinDigits);
                return;
            }
        } else {
            const inputs = form.querySelectorAll('input[required]');
            let allValid = true;

            inputs.forEach(input => {
                const isValid = validateField({ target: input });
                if (!isValid) allValid = false;
            });

            if (!allValid) {
                event.preventDefault();
                return;
            }
        }

        // Enter-key submits arrive without a button in some flows (stepped login).
        if (!submitBtn) return;
        submitBtn.textContent = 'Signing In...';
        submitBtn.classList.add('loading');
        submitBtn.disabled = true;
    }

    showLoginError(message) {
        const existingError = document.querySelector('.login-error');
        if (existingError) {
            existingError.remove();
        }

        const errorElement = document.createElement('div');
        errorElement.className = 'error login-error';
        errorElement.textContent = message;

        const loginForm = document.querySelector('.login-form');
        if (loginForm) {
            const title = loginForm.querySelector('h2');
            if (title) {
                title.insertAdjacentElement('afterend', errorElement);
            }
        }

        setTimeout(() => {
            errorElement.remove();
        }, 5000);
    }

    async makeAuthenticatedRequest(url, options = {}) {
        const defaultOptions = {
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const mergedOptions = {
            ...defaultOptions,
            ...options,
            headers: { ...defaultOptions.headers, ...options.headers }
        };

        try {
            const response = await fetch(url, mergedOptions);

            if (response.status === 401) {
                window.location.href = '/login';
                return null;
            }

            return response;
        } catch (error) {
            console.error('Request failed:', error);
            throw error;
        }
    }
}
